import React, { useState } from "react";
import { Tabs, Table, Tag, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import { MessageCircle, Eye, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";

const CustomerInteraction = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("message");

    // ===== DATA =====
    const messageData = [
        {
            key: 1,
            name: "Nguyễn Văn A",
            phone: "0901234567",
            email: "nguyenvana@gmail.com",
            message: "Cho mình hỏi sản phẩm này còn hàng không ạ?",
            campaign: "...",
            count: 3,
            time: "10:30 03/04/2026",
            status: "done",
        },
    ];

    const clickData = [
        {
            key: 1,
            name: "Phạm Thị D",
            phone: "0934567890",
            campaign: "...",
            count: 7,
            time: "14:20 03/04/2026",
            status: "new",
        },
    ];

    const commentData = [
        {
            key: 1,
            name: "Đỗ Văn G",
            phone: "0967890123",
            comment: "Sản phẩm trông có vẻ ổn đấy!",
            campaign: "...",
            count: 2,
            time: "13:45 03/04/2026",
            status: "new",
        },
    ];

    // ===== COMMON UI =====
    const renderAvatar = (name: string) => {
        return (
            <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-semibold">
                {name.charAt(0)}
            </div>
        );
    };

    const renderStatus = (status: string) => {
        if (status === "new")
            return <Tag className="bg-blue-100 text-blue-700 border-none">{t("ads.status_new")}</Tag>;
        if (status === "done")
            return <Tag className="bg-yellow-100 text-yellow-700 border-none">{t("ads.status_contacted")}</Tag>;
        return <Tag className="bg-green-100 text-green-700 border-none">{t("ads.status_converted")}</Tag>;
    };

    // ===== TABLE 1: MESSAGE =====
    const messageColumns = [
        {
            title: t("ads.col_customer_name"),
            render: (_: any, r: any) => (
                <div className="flex items-center gap-3">
                    {renderAvatar(r.name)}
                    {r.name}
                </div>
            ),
        },
        {
            title: t("ads.col_contact"),
            render: (_: any, r: any) => (
                <div className="text-sm text-gray-500">
                    <div>{r.phone}</div>
                    {r.email && <div>{r.email}</div>}
                </div>
            ),
        },
        { title: t("ads.col_message"), dataIndex: "message" },
        { title: t("ads.col_campaign"), dataIndex: "campaign" },
        { title: t("ads.col_interaction_count"), dataIndex: "count", align: "center" },
        { title: t("ads.col_last_time"), dataIndex: "time" },
        {
            title: t("ads.col_status"),
            render: (_: any, r: any) => renderStatus(r.status),
        },
        {
            title: t("ads.col_actions"),
            render: () => <Button size="small">{t("ads.btn_contact")}</Button>,
        },
    ];

    // ===== TABLE 2: CLICK =====
    const clickColumns = [
        {
            title: t("ads.col_customer_name"),
            render: (_: any, r: any) => (
                <div className="flex items-center gap-3">
                    {renderAvatar(r.name)}
                    {r.name}
                </div>
            ),
        },
        {
            title: t("ads.col_contact"),
            render: (_: any, r: any) => <div>{r.phone}</div>,
        },
        { title: t("ads.col_campaign"), dataIndex: "campaign" },
        { title: t("ads.col_interaction_count"), dataIndex: "count", align: "center" as const },
        { title: t("ads.col_last_time"), dataIndex: "time" },
        {
            title: t("ads.col_status"),
            render: (_: any, r: any) => renderStatus(r.status),
        },
        {
            title: t("ads.col_actions"),
            render: () => <Button size="small">{t("ads.btn_contact")}</Button>,
        },
    ];

    // ===== TABLE 3: COMMENT =====
    const commentColumns = [
        {
            title: t("ads.col_customer_name"),
            render: (_: any, r: any) => (
                <div className="flex items-center gap-3">
                    {renderAvatar(r.name)}
                    {r.name}
                </div>
            ),
        },
        {
            title: t("ads.col_contact"),
            render: (_: any, r: any) => <div>{r.phone}</div>,
        },
        { title: t("ads.col_comment"), dataIndex: "comment" },
        { title: t("ads.col_campaign"), dataIndex: "campaign" },
        { title: t("ads.col_interaction_count"), dataIndex: "count", align: "center" },
        { title: t("ads.col_last_time"), dataIndex: "time" },
        {
            title: t("ads.col_status"),
            render: (_: any, r: any) => renderStatus(r.status),
        },
        {
            title: t("ads.col_actions"),
            render: () => <Button size="small">{t("ads.btn_contact")}</Button>,
        },
    ];

    return (
        <div className="bg-white rounded-xl p-4 border dark:bg-[#334155] dark:text-white dark:border-gray-600">

            <h3 className="font-semibold mb-3 text-000000e0">{t("ads.customer_interaction")}</h3>

            <Tabs activeKey={activeTab} onChange={setActiveTab} className="custom-tabs dark-tabs">

                <Tabs.TabPane
                    tab={<span className="flex gap-2 items-center"><MessageCircle size={16} /> {t("ads.tab_message")} (3)</span>}
                    key="message"
                >
                    <Table
                        columns={messageColumns} dataSource={messageData} pagination={false}
                        scroll={{ x: "max-content" }}
                        className="custom-ant-table"
                    />
                </Tabs.TabPane>

                <Tabs.TabPane
                    tab={<span className="flex gap-2 items-center"><Eye size={16} /> Click (3)</span>}
                    key="click"
                >
                    <Table
                        columns={clickColumns} dataSource={clickData} pagination={false}
                        scroll={{ x: "max-content" }}
                        className="custom-ant-table"
                    />
                </Tabs.TabPane>

                <Tabs.TabPane
                    tab={<span className="flex gap-2 items-center"><MessageSquare size={16} /> {t("ads.tab_comment")} (3)</span>}
                    key="comment"
                >
                    <Table
                        columns={commentColumns}
                        dataSource={commentData}
                        pagination={false}
                        scroll={{ x: "max-content" }}
                        className="custom-ant-table"
                    />
                </Tabs.TabPane>

            </Tabs>
        </div>
    );
};

export default CustomerInteraction;